import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BackofficeLayout from '../../components/backoffice/BackofficeLayout';
import DataTable from '../../components/backoffice/DataTable';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { requestService } from '../../services/requestService';
import { Eye, Mail, Phone, Calendar, MessageSquare, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const formatDate = (dateStr, fmt = 'dd MMM yyyy') => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return format(date, fmt, { locale: fr });
  } catch {
    return '-';
  }
};

const Requests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  useEffect(() => {
    loadRequests();
  }, [filter, pagination.page]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const queryFilters = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (filter !== 'all') {
        queryFilters.statut = filter;
      }

      const response = await requestService.getAllRequests(queryFilters);
      setRequests(response.data || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (error) {
      console.error('Error loading requests:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await requestService.updateRequest(requestId, { statut: newStatus });
      toast.success('Statut mis à jour avec succès');
      loadRequests();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, statut: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const openDetailDialog = (request) => {
    setSelectedRequest(request);
    setDetailDialog(true);
  };

  const getStatusBadge = (statut) => {
    const statusConfig = {
      'Nouveau': { label: 'Nouveau', className: 'bg-blue-100 text-blue-800' },
      'En cours': { label: 'En cours', className: 'bg-yellow-100 text-yellow-800' },
      'Traité': { label: 'Traité', className: 'bg-green-100 text-green-800' },
      'Clos': { label: 'Clos', className: 'bg-slate-100 text-slate-800' }
    };
    const config = statusConfig[statut] || { label: statut || 'Inconnu', className: 'bg-slate-100 text-slate-800' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      'Contact': { label: 'Contact', className: 'bg-purple-100 text-purple-800' },
      'Bien': { label: 'Bien', className: 'bg-orange-100 text-orange-800' },
      'Service': { label: 'Service', className: 'bg-cyan-100 text-cyan-800' }
    };
    const config = typeConfig[type] || { label: type || 'Autre', className: 'bg-indigo-100 text-indigo-800' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const columns = [
    {
      header: 'Client',
      cell: (row) => (
        <div>
          <div className="font-medium">{row.nomClient} {row.prenomClient || ''}</div>
          <div className="text-sm text-slate-500">{row.emailClient || '-'}</div>
        </div>
      )
    },
    {
      header: 'Type',
      accessorKey: 'typeDemande',
      cell: (row) => getTypeBadge(row.typeDemande)
    },
    {
      header: 'Téléphone',
      accessorKey: 'telephoneClient',
      cell: (row) => (
        <span className="text-sm">{row.telephoneClient || 'N/A'}</span>
      )
    },
    {
      header: 'Statut',
      accessorKey: 'statut',
      cell: (row) => (
        <Select
          value={row.statut}
          onValueChange={(value) => handleStatusChange(row.id, value)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Nouveau">Nouveau</SelectItem>
            <SelectItem value="En cours">En cours</SelectItem>
            <SelectItem value="Traité">Traité</SelectItem>
            <SelectItem value="Clos">Clos</SelectItem>
          </SelectContent>
        </Select>
      )
    },
    {
      header: 'Date',
      accessorKey: 'dateDemande',
      cell: (row) => (
        <span className="text-sm text-slate-600">
          {formatDate(row.dateDemande)}
        </span>
      )
    },
    {
      header: 'Actions',
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openDetailDialog(row)}
        >
          <Eye className="w-4 h-4 mr-1" />
          Voir
        </Button>
      ),
      className: 'text-right'
    }
  ];

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Gestion des Demandes</h1>
            <p className="text-slate-600 mt-1">
              {pagination.total} demande{pagination.total > 1 ? 's' : ''} au total
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filtrer par statut</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={filter} onValueChange={(value) => {
              setFilter(value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="Nouveau">Nouveau</SelectItem>
                <SelectItem value="En cours">En cours</SelectItem>
                <SelectItem value="Traité">Traité</SelectItem>
                <SelectItem value="Clos">Clos</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Table */}
        <DataTable
          columns={columns}
          data={requests}
          loading={loading}
          emptyMessage="Aucune demande trouvée"
        />

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Page {pagination.page} sur {Math.ceil(pagination.total / pagination.limit)}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détail de la demande</DialogTitle>
            <DialogDescription>
              Informations complètes sur la demande client
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6">
              {/* Client Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Informations client</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <span className="text-amber-700 font-semibold">
                        {selectedRequest.nomClient?.charAt(0)}{selectedRequest.prenomClient?.charAt(0) || ''}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium">
                        {selectedRequest.nomClient} {selectedRequest.prenomClient || ''}
                      </div>
                      <div className="text-sm text-slate-500">{getTypeBadge(selectedRequest.typeDemande)}</div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 pt-4">
                    {selectedRequest.emailClient && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{selectedRequest.emailClient}</span>
                      </div>
                    )}
                    {selectedRequest.telephoneClient && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{selectedRequest.telephoneClient}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-sm pt-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span>
                      Reçue le {formatDate(selectedRequest.dateDemande, 'dd MMMM yyyy à HH:mm')}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Request Details */}
              {selectedRequest.message && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      Message
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 whitespace-pre-wrap">
                      {selectedRequest.message}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Status Update */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Statut de la demande</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-600">Changer le statut :</span>
                    <Select
                      value={selectedRequest.statut}
                      onValueChange={(value) => handleStatusChange(selectedRequest.id, value)}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Nouveau">Nouveau</SelectItem>
                        <SelectItem value="En cours">En cours</SelectItem>
                        <SelectItem value="Traité">Traité</SelectItem>
                        <SelectItem value="Clos">Clos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Property Reference if available */}
              {selectedRequest.idBienConcerne && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Bien concerné</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600">
                      ID du bien: {selectedRequest.idBienConcerne}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </BackofficeLayout>
  );
};

export default Requests;
